import { t } from "@/lib/i18n/interpolate";

export type HookType =
  | "question"
  | "statistic"
  | "quotation"
  | "anecdote"
  | "bold";

/**
 * Hook frames. A frame never puts a verb or pronoun after the topic, so singular and
 * plural topics ("teenage sleep", "school uniforms") both read correctly. `{topic}` marks
 * where the writer's topic goes; a translated content file replaces the frames through
 * `ui`.
 */
export const HOOK_FRAMES: Record<HookType, readonly string[]> = {
  question: [
    "What would change if {topic} disappeared overnight?",
    "Why should anyone care about {topic} today?",
    "How much do most people really understand about {topic}?",
  ],
  statistic: [
    "Every year, [number] people are affected by {topic} — a figure most of us never hear about.",
    "According to [source], [statistic]. That single number reframes the entire debate over {topic}.",
    "Recent research shows that [statistic], and that trend runs straight through {topic}.",
  ],
  quotation: [
    '"[Insert a relevant quotation]," wrote [author] — words that capture the heart of {topic}.',
    '[Author] once observed that "[quotation]." Few ideas apply more directly to {topic}.',
    'As [expert] put it, "[quotation]" — a claim this essay tests against {topic}.',
  ],
  anecdote: [
    "The first time I encountered {topic}, I had no idea that the encounter would change how I saw [related idea].",
    "Picture [a specific person] facing {topic} for the first time — that moment is where this essay begins.",
    "On an otherwise ordinary [day or moment], [someone] came face to face with {topic} — and nothing about that moment was ordinary.",
  ],
  bold: [
    "A second look is overdue for almost everything we assume about {topic}.",
    "Few subjects are as widely misunderstood as {topic}.",
    "A challenge is overdue for the conventional wisdom about {topic}.",
  ],
};

/** Fills the hook frames of one type with the writer's topic. */
export function hooksFor(
  topic: string,
  hookType: HookType,
  frames: readonly string[] = HOOK_FRAMES[hookType]
): string[] {
  return frames.map((frame) => t(frame, { topic }));
}
